import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import {
  forkJoin,
  map,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { AlertStatus } from 'src/app/core/models/alert.model';
import {
  Asset,
  Company,
  HierarchyItem,
  HierarchyType,
  MonitoringPoint,
  Region,
  Site,
  Sublocation
} from 'src/app/core/models/hierarchy.model';
import { IconPipe } from 'src/app/core/pipes/icon.pipe';
import { TrackByPropertyPipe } from 'src/app/core/pipes/track-by-property.pipe';
import { FlagService } from 'src/app/core/services/flag.service';
import { HierarchyService } from 'src/app/core/services/hierarchy.service';
import { TenantService } from 'src/app/core/services/tenant.service';
import { UserInfoService } from 'src/app/core/services/user-info.service';
import { Badge } from 'src/app/shared/badge-container/badge-container.model';
import { FilterBadgeComponent } from 'src/app/shared/filter-badge/filter-badge.component';

@Component({
  selector: 'hierarchy-popover',
  templateUrl: './hierarchy-popover.component.html',
  styleUrls: ['./hierarchy-popover.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FilterBadgeComponent,
    IconPipe,
    AccordionModule,
    MatTooltipModule,
    RouterModule,
    TrackByPropertyPipe,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    FlexLayoutModule
  ]
})
export class HierarchyPopoverComponent implements OnInit, OnDestroy {
  @Input() hierarchyTopCoordinate: number;
  @ViewChild('accordionWrapper') accordionWrapper: ElementRef;
  @Output() closeMenu = new EventEmitter<boolean>();

  company: Company;
  region: Region;
  site: Site;
  sublocations: Sublocation[];

  fetchingHierarchy = false;

  badges: Badge[] = [];
  searchInput = '';
  searchControl: FormControl = new FormControl('');
  emptyFilters = true;

  maxPopoverHeight: number;
  bottomHierarchyPadding = 250;

  readonly AlertStatus = AlertStatus;

  private ngUnsubscribe = new Subject<void>();

  regionStream$ = this.hierarchyService.hasAssignedRegion$.pipe(
    map((res) => ({ hasRegions: res })),
    takeUntil(this.ngUnsubscribe)
  );

  resetPopoverSubject = new Subject<void>();
  resetPopover$ = this.resetPopoverSubject.pipe(
    startWith(null),
    tap(() => (this.fetchingHierarchy = true)),
    switchMap(() =>
      forkJoin([
        this.hierarchyService.fetchAssetsV2(),
        this.hierarchyService.fetchMonitoringPointsV2(
          this.hierarchyService.getSelectedSiteV2().id
        )
      ])
    ),
    tap(([assets, monitoringPoints]) => {
      this.badges = [AlertStatus.Critical, AlertStatus.Warning].map(
        (level) => ({
          active: false,
          count: 0,
          title: level
        })
      );

      this.site = this.hierarchyService.getSelectedSiteV2();
      this.company = this.site.breadcrumb[HierarchyType.Company] as Company;
      this.region = this.site.breadcrumb[HierarchyType.Region] as Region;

      this.sublocations = this.site.sublocations
        .map((sl) => ({
          ...sl,
          assets: assets
            .filter(
              (a) => a.breadcrumb[HierarchyType.Sublocation]?.id === sl.id
            )
            .map((a) => ({
              ...a,
              monitoringPoints: monitoringPoints.filter(
                (mp) => mp.breadcrumb[HierarchyType.Asset]?.id === a.id
              )
            }))
        }))
        .filter((sl) =>
          assets
            .filter(
              (a) => a.breadcrumb[HierarchyType.Sublocation]?.id === sl.id
            )
            .some((a) => !a.inactive)
        ) as Sublocation[];

      this.expandAllHierarchy(false);
      this.filterHierarchy();
      this.calculatePauseStatuses();

      this.fetchingHierarchy = false;

      setTimeout(() => {
        this.calculateMaxPopoverHeight();
        this.adjustPopoverHeight();
      }, 10);
    })
  );

  constructor(
    private hierarchyService: HierarchyService,
    private router: Router,
    public tenantService: TenantService,
    public flagService: FlagService,
    private userInfoService: UserInfoService
  ) {}

  ngOnInit() {
    this.site = this.hierarchyService.getSelectedSiteV2();
    this.resetPopoverSubject.next();

    this.hierarchyService.selectedSite$
      .pipe(
        tap(() => {
          this.resetPopoverSubject.next();
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();

    this.userInfoService.info$
      .pipe(
        tap(() => {
          this.resetPopoverSubject.next();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.calculateMaxPopoverHeight();
    this.adjustPopoverHeight();
  }

  navigateToHierarchyItem(item: HierarchyItem) {
    const { type, id } = item;

    switch (type) {
      case HierarchyType.Company:
      case HierarchyType.Region:
      case HierarchyType.Site:
      case HierarchyType.Sublocation:
        this.router.navigate(['dashboard', id]);
        break;
      case HierarchyType.Asset:
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this.router.navigate(['/assets', id, 'health']);
          });
        break;
      case HierarchyType.MonitoringPoint: {
        const { assetId } = item as MonitoringPoint;
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this.router.navigate(['/assets', assetId, 'telemetry'], {
              queryParams: { selected: id }
            });
          });
        break;
      }
      default:
        this.router.navigate(['/']);
        break;
    }

    this.closeMenu.emit(true);
  }

  onSearchInput(input: string) {
    this.searchInput = input.toLowerCase().trim();
    this.filterHierarchy();
  }

  onBadgeClick(badge: Badge) {
    badge.active = !badge.active;
    this.filterHierarchy();
  }

  filterHierarchy() {
    this.emptyFilters =
      this.searchInput.length === 0 &&
      this.badges.filter(({ active }) => active).length === 0;

    const matchesFilters = (item: HierarchyItem) =>
      this.emptyFilters ||
      (this.matchesSearchText(item.name) && this.matchesBadges(item));

    this.site.meta['visibleSublocations'] = 0;
    for (const sublocation of this.sublocations) {
      sublocation.meta['matchesFilter'] = matchesFilters(sublocation);
      sublocation.meta['childMatchesFilter'] = false;

      for (const asset of sublocation.assets) {
        asset.meta['matchesFilter'] = matchesFilters(asset);
        asset.meta['childMatchesFilter'] = false;

        if (asset.meta['matchesFilter']) {
          sublocation.meta['childMatchesFilter'] = true;
        }

        for (const monitoringPoint of asset.monitoringPoints) {
          monitoringPoint.meta['matchesFilter'] =
            matchesFilters(monitoringPoint);

          if (monitoringPoint.meta['matchesFilter']) {
            sublocation.meta['childMatchesFilter'] = asset.meta[
              'childMatchesFilter'
            ] = true;
          }
        }
      }

      if (
        sublocation.meta['matchesFilter'] ||
        sublocation.meta['childMatchesFilter']
      ) {
        this.site.meta['visibleSublocations']++;
      }
    }
  }

  matchesSearchText(name: string) {
    return name.trim().toLowerCase().includes(this.searchInput);
  }

  matchesBadges(item: HierarchyItem) {
    switch (item.type) {
      case HierarchyType.Asset:
      case HierarchyType.MonitoringPoint: {
        const activeBadges = this.badges.filter(({ active }) => active);

        return (
          !activeBadges.length ||
          activeBadges.some(
            ({ title }) =>
              title === (item as Asset | MonitoringPoint).alertStatus
          )
        );
      }
      case HierarchyType.Sublocation:
        return !!this.searchInput.length;
      default:
        return false;
    }
  }

  expandAllHierarchy(value: boolean) {
    for (const sublocation of this.sublocations) {
      sublocation.meta['isExpanded'] = value;

      for (const asset of sublocation.assets) {
        asset.meta['isExpanded'] = value;
      }
    }
    this.resizeOnExpand();
  }

  toggleHierarchyItem(item: HierarchyItem) {
    item.meta['isExpanded'] = !item.meta['isExpanded'];
    this.resizeOnExpand();
  }

  isTooltipDisabled(e: HTMLSpanElement) {
    return e.scrollWidth <= e.clientWidth;
  }

  resizeOnExpand(): void {
    this.calculateMaxPopoverHeight();
    this.adjustPopoverHeight();
  }

  calculateMaxPopoverHeight() {
    const windowHeight = window.innerHeight;
    this.maxPopoverHeight =
      windowHeight - this.hierarchyTopCoordinate - this.bottomHierarchyPadding;
  }

  adjustPopoverHeight() {
    const contentElement = this.accordionWrapper?.nativeElement;

    if (contentElement) {
      contentElement.style.maxHeight = `${this.maxPopoverHeight}px`;
    }
  }

  calculatePauseStatuses() {
    const { start, end } = this.site.pauseAlertingWindow;
    const now = new Date();

    this.site.meta['isPaused'] = now >= start && now <= end;

    for (const sublocation of this.sublocations) {
      const { start, end } = sublocation.pauseAlertingWindow;
      sublocation.meta['isPaused'] = now >= start && now <= end;

      for (const asset of sublocation.assets) {
        const { start, end } = asset.pauseAlertingWindow;
        asset.meta['isPaused'] = now >= start && now <= end;

        for (const monitoringPoint of asset.monitoringPoints) {
          const { start, end } = monitoringPoint.pauseAlertingWindow;
          monitoringPoint.meta['isPaused'] = now >= start && now <= end;
        }
      }
    }
  }
}
